const UTM_STORAGE_KEY = 'ruut_utm_params';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * Capture UTM parameters from the current URL
 * @returns {Object} Object containing UTM parameters
 */
export const captureUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};

  UTM_PARAMS.forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });

  return utmParams;
};

/**
 * Store UTM parameters in localStorage (only if not already stored)
 * This ensures first-touch attribution
 */
export const storeUtmParams = () => {
  const existingParams = getStoredUtmParams();

  // Only store if we don't already have UTM params (first-touch attribution)
  if (Object.keys(existingParams).length === 0) {
    const utmParams = captureUtmParams();

    if (Object.keys(utmParams).length > 0) {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    }
  }
};

/**
 * Get stored UTM parameters from localStorage
 * @returns {Object} Stored UTM parameters
 */
export const getStoredUtmParams = () => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
};

/**
 * Clear stored UTM parameters
 */
export const clearUtmParams = () => {
  localStorage.removeItem(UTM_STORAGE_KEY);
};

/**
 * Get UTM parameters for API submission
 * Checks URL first, then falls back to localStorage
 * @returns {Object} UTM parameters to send to API
 */
export const getUtmParamsForSubmission = () => {
  const urlParams = captureUtmParams();

  // If URL has UTM params, use those (and update storage)
  if (Object.keys(urlParams).length > 0) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(urlParams));
    return urlParams;
  }

  // Otherwise, fall back to stored params
  return getStoredUtmParams();
};

/**
 * Append UTM parameters to a URL
 * @param {string} url - Base URL
 * @returns {string} URL with UTM parameters appended
 */
export const appendUtmParamsToUrl = (url) => {
  const utmParams = getStoredUtmParams();

  if (Object.keys(utmParams).length === 0) {
    return url;
  }

  const urlObj = new URL(url, window.location.origin);

  Object.entries(utmParams).forEach(([key, value]) => {
    urlObj.searchParams.set(key, value);
  });

  return urlObj.toString();
};

/**
 * Check if user has UTM parameters (either in URL or storage)
 * @returns {boolean}
 */
export const hasUtmParams = () => {
  return Object.keys(captureUtmParams()).length > 0 ||
         Object.keys(getStoredUtmParams()).length > 0;
};
